import type { YMap, LngLat } from '@yandex/ymaps3-types'
import type { Component } from 'vue'

import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'

import type {
    ClusterFeatureEntity,
    DispatcherWorkplaceDataEntity,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceCourierLocation,
    DispatcherWorkplaceDelivery,
    DispatcherWorkplaceRenderingEntity,
    CreateClusterPopupListeners,
    DispatcherWorkplaceCourier
} from '@/modules/DispatcherWorkplaceModule/types'

import { createApp, h } from 'vue'

import DwRecordMarker from '@/modules/DispatcherWorkplaceModule/components/DwRecordMarker/index.vue'
// import DwProblemMarker from '@/modules/DispatcherWorkplaceModule/components/DwProblemMarker/index.vue'
import DwClusterMarker from '@/modules/DispatcherWorkplaceModule/components/DwClusterMarker/index.vue'
import DwCourierStartIcon from '@/modules/DispatcherWorkplaceModule/components/DwCourierStartIcon/index.vue'
import DwCourierLastIcon from '@/modules/DispatcherWorkplaceModule/components/DwCourierLastIcon/index.vue'

import DwClusterPopup from '@/modules/DispatcherWorkplaceModule/components/DwClusterPopup/index.vue'
import DwRecordPopup from '@/modules/DispatcherWorkplaceModule/components/DwRecordPopup/index.vue'

import {
    TAKE_MARKER_COLOR,
    UNASSIGNED_TAKE_MARKER_COLOR,
    DELIVERY_MARKER_COLOR,
    UNDELIVERED_DELIVERY_MARKER_COLOR,
    PROBLEM_MARKER_COLOR,
} from './constants'

type UpdateMapLocationOptions = {
    map: YMap
    coordinates: LngLat
    zoom: number
}

export function convertCourierToStartIcon(courier: DispatcherWorkplaceCourier): HTMLElement {
    const element = document.createElement('div')
    element.classList.add('dw-marker-animation')

    if (courier.firstAction.length) {
        const app = createApp({
            render: () => h(DwCourierStartIcon, {})
        })

        app.mount(element)
    }

    return element
}

export function convertCourierToLastIcon(courier: DispatcherWorkplaceCourier): HTMLElement {
    const element = document.createElement('div')
    element.classList.add('dw-marker-animation')

    if (courier.locations.length) {
        const app = createApp({
            render: () => h(DwCourierLastIcon, { color: courier.localColor })
        })

        app.mount(element)
    }

    return element
}

export function convertFeatureToMarkerElement(feature: ClusterFeature<ClusterFeatureEntity>, isCourierItem = false): HTMLElement {
    const element = document.createElement('div')
    element.classList.add('dw-marker-animation')

    if (feature.entity) {
        const app = createApp({
            render: () => h(DwRecordMarker, {
                isCourierItem,
                entity: feature.entity as DispatcherWorkplaceDataEntity
            })
        })
    
        app.mount(element)
    }
    // else if (feature.location) {
    //     const app = createApp({
    //         render: () => h(DwProblemMarker, {
    //             downtime: (feature.location as DispatcherWorkplaceCourierLocation).downtime as number
    //         })
    //     })
    
    //     app.mount(element)
    // }

    return element
}

export function calculateClusterSegments(features: Array<ClusterFeature<ClusterFeatureEntity>>): { percentage: number; color: string; }[] {
    let takes = 0
    let unassigned_takes = 0

    let deliveries = 0
    let undelivered_deliveries = 0

    let problems = 0
    let total = 0

    features.forEach((feature) => {
        if (feature.entity) {
            if (feature.entity.type === 1) {
                if ((feature.entity as DispatcherWorkplaceTake).notAssigned) {
                    unassigned_takes = unassigned_takes + 1
                } else {
                    takes = takes + 1
                }
            }
    
            if (feature.entity.type === 2) {
                if ((feature.entity as DispatcherWorkplaceDelivery).notDelivered) {
                    undelivered_deliveries = undelivered_deliveries + 1
                } else {
                    deliveries = deliveries + 1
                }
            }
        }

        if (feature.location) {
            problems = problems + 1
        }
    })

    total = takes + unassigned_takes + deliveries + undelivered_deliveries + problems
    if (total === 0) return []

    let takesPercentage = Math.round((takes / total) * 100)
    let unassignedTakesPercentage = Math.round((unassigned_takes / total) * 100)
    let deliveriesPercentage = Math.round((deliveries / total) * 100)
    let unDeliveredDeliveriesPercentage = Math.round((undelivered_deliveries / total) * 100)
    let problemsPercentage = Math.round((problems / total) * 100)

    return [
        { percentage: takesPercentage, color: TAKE_MARKER_COLOR },
        { percentage: unassignedTakesPercentage, color: UNASSIGNED_TAKE_MARKER_COLOR },
        { percentage: deliveriesPercentage, color: DELIVERY_MARKER_COLOR },
        { percentage: unDeliveredDeliveriesPercentage, color: UNDELIVERED_DELIVERY_MARKER_COLOR },
        { percentage: problemsPercentage, color: PROBLEM_MARKER_COLOR }
    ]
}

export function convertFeaturesToClusterElement(features: Array<ClusterFeature<ClusterFeatureEntity>>): HTMLElement {
    const element = document.createElement('div')
    element.classList.add('dw-marker-animation')

    const app = createApp({
        render: () => h(DwClusterMarker, {
            size: 50,
            number: features.length,
            segments: calculateClusterSegments(features),
            features
        })
    })

    app.mount(element)

    return element
}

export function convertOrderToClusterFeature(order: DispatcherWorkplaceDataEntity): ClusterFeature<ClusterFeatureEntity> {
    let id = ''

    if (order.type === 1) {
        id = `order-${(order as DispatcherWorkplaceTake).orderNumber}`
    }

    if (order.type === 2) {
        id = `invoice-${(order as DispatcherWorkplaceDelivery).invoiceNumber}`
    }

    return {
        id,
        type: 'Feature',
        geometry: {
            type: 'Point' as 'Point',
            coordinates: [Number(order.longitude as string), Number(order.latitude as string)] as LngLat
        },
        properties: {
            name: 'marker',
            description: '',
        },
        entity: order,
    }
}

export function convertOrderOrLocationToClusterFeature(orderOrLocation: DispatcherWorkplaceRenderingEntity): ClusterFeature<ClusterFeatureEntity> {
    // if (orderOrLocation.hasOwnProperty('downtime')) {
    //     const location = orderOrLocation as DispatcherWorkplaceCourierLocation

    //     return {
    //         id: `location-${location.id}`,
    //         type: 'Feature',
    //         geometry: {
    //             type: 'Point' as 'Point',
    //             coordinates: [Number(location.longitude as string), Number(location.latitude as string)] as LngLat
    //         },
    //         properties: {
    //             name: 'marker',
    //             description: '',
    //         },
    //         location: location,
    //     }
    // }

    const order = orderOrLocation as DispatcherWorkplaceDataEntity

    let id = ''

    if (order.type === 1) {
        id = `order-${(order as DispatcherWorkplaceTake).orderNumber}`
    }

    if (order.type === 2) {
        id = `invoice-${(order as DispatcherWorkplaceDelivery).invoiceNumber}`
    }

    return {
        id,
        type: 'Feature',
        geometry: {
            type: 'Point' as 'Point',
            coordinates: [Number(order.longitude as string), Number(order.latitude as string)] as LngLat
        },
        properties: {
            name: 'marker',
            description: '',
        },
        entity: order,
    }
}

export function createClusterPopup(
    coordinates: LngLat,
    features: Array<ClusterFeatureEntity>,
    selectedFeatureId: number | null,
    close: () => void,
    listeners?: CreateClusterPopupListeners,
    isCourierItem = false
): HTMLElement {
    const element = document.createElement('div')

    const app = createApp({
        render: () => h(DwClusterPopup as Component, {
            features,
            selectedFeatureId,
            isCourierItem,
            onClose: close,
            onChangeDate: listeners?.changeDate,
            onAddToWaitList: listeners?.addToWaitList,
            onAssign: listeners?.assign,
            onSelfDelivery: listeners?.selfDelivery
        })
    })

    app.mount(element)

    return element
}

export function createMarkerPopup(
    feature: ClusterFeatureEntity,
    close: () => void,
    listeners?: CreateClusterPopupListeners
): HTMLElement {
    const element = document.createElement('div')

    const app = createApp({
        render: () => h(DwRecordPopup as Component, {
            feature,
            onClose: close,
            onChangeDate: listeners?.changeDate,
            onAddToWaitList: listeners?.addToWaitList,
            onAssign: listeners?.assign,
            onSelfDelivery: listeners?.selfDelivery
        })
    })

    app.mount(element)

    return element
}

export function updateMapLocation(options: UpdateMapLocationOptions) {
    options.map.update({
        location: {
            center: options.coordinates,
            duration: 500,
            easing: 'ease-in',
            zoom: options.zoom,
        },
        camera: {
            tilt: (45 * Math.PI) / 180,
        },
    })
}

export default {
    convertCourierToStartIcon,
    convertCourierToLastIcon,
    convertFeatureToMarkerElement,
    convertFeaturesToClusterElement,
    calculateClusterSegments,
    convertOrderToClusterFeature,
    convertOrderOrLocationToClusterFeature,
    createClusterPopup,
    createMarkerPopup,
    updateMapLocation
}